<template>
  <div class="tooltip-wrapper" @mouseenter="show" @mouseleave="hide">
    <!-- 插槽：被包裹的内容 -->
    <slot></slot>

    <!-- 气泡 -->
    <transition name="fade">
      <div v-if="visible" class="tooltip-content" :style="contentStyle">
        {{ content }}
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  content: string // 提示文字
  position?: 'top' | 'bottom' | 'left' | 'right' // 位置，默认 top
  offset?: number // 偏移距离，默认 8px
  delay?: number // 延迟显示(ms)，默认 300
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top',
  offset: 8,
  delay: 300,
})

const visible = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

const show = () => {
  timer = setTimeout(() => {
    visible.value = true
  }, props.delay)
}

const hide = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  visible.value = false
}

// 根据位置计算样式
const contentStyle = computed(() => {
  const style: Record<string, string> = {}

  switch (props.position) {
    case 'top':
      style.bottom = `calc(100% + ${props.offset}px)`
      style.left = '50%'
      style.transform = 'translateX(-50%)'
      break
    case 'bottom':
      style.top = `calc(100% + ${props.offset}px)`
      style.left = '50%'
      style.transform = 'translateX(-50%)'
      break
    case 'left':
      style.right = `calc(100% + ${props.offset}px)`
      style.top = '50%'
      style.transform = 'translateY(-50%)'
      break
    case 'right':
      style.left = `calc(100% + ${props.offset}px)`
      style.top = '50%'
      style.transform = 'translateY(-50%)'
      break
  }

  return style
})
</script>

<style scoped>
.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip-content {
  position: absolute;
  padding: 6px 12px;
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
}

/* 可选：小箭头 */
.tooltip-content::before {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border: 5px solid transparent;
}

/* 根据不同位置显示箭头 */
.tooltip-content[style*='top']::before {
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-bottom-color: rgba(0, 0, 0, 0.75);
}

.tooltip-content[style*='bottom']::before {
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-top-color: rgba(0, 0, 0, 0.75);
}

/* 动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
